import { fuego } from '@store/firebase';
import { CollectionPath } from '@typings/collections.enum';
import { IPhotoCard, IDispatchHistory } from '@typings/photocard/photocard.interface';
import { ExplorationArea, InvestigationText } from '@typings/worldmap/explorationArea.interface';
import { getItemData, parchaseItem } from '@store/firebase/store/parchase-item';

/**
 * 파견 시작하기
 * @param characterId - 파견할 캐릭터 ID
 * @param collectionPath - 캐릭터 컬렉션 경로
 * @param areaId - 파견 지역 ID
 * @param duration - 파견 소요 시간 (분)
 */
export async function startDispatch({
  characterId,
  collectionPath,
  areaId,
  areaName,
  duration,
}: {
  characterId: string;
  collectionPath: string;
  areaId: string;
  areaName: string;
  duration: number;
}): Promise<boolean> {
  try {
    const characterRef = fuego.db.collection(collectionPath).doc(characterId);
    const now = new Date();

    await characterRef.update({
      dispatchAreaId: areaId,
      isDispatched: true,
      dispatchStartedAt: now,
      dispatchDuration: duration,
    });

    console.log(`✅ 캐릭터 ${characterId} 파견 시작: ${areaName} (${duration}분)`);
    return true;
  } catch (error) {
    console.error('파견 시작 실패:', error);
    throw error;
  }
}

/**
 * 파견 완료 처리
 * - 파견 히스토리에 추가
 * - 보상 지급 (랜덤 조사지문 + 아이템)
 * - 파견 상태 초기화
 */
export async function completeDispatch({
  characterId,
  collectionPath,
  userId,
}: {
  characterId: string;
  collectionPath: string;
  userId: string;
}): Promise<IDispatchHistory | null> {
  try {
    const characterRef = fuego.db.collection(collectionPath).doc(characterId);
    const characterDoc = await characterRef.get();

    if (!characterDoc.exists) {
      console.error('캐릭터를 찾을 수 없습니다:', characterId);
      return null;
    }

    const characterData = characterDoc.data() as IPhotoCard;
    
    if (!characterData.isDispatched || !characterData.dispatchAreaId) {
      console.error('파견 중인 캐릭터가 아닙니다:', characterId);
      return null;
    }

    // 파견 지역 정보 가져오기
    const areaRef = fuego.db.collection(CollectionPath.WORLDMAP).doc(characterData.dispatchAreaId);
    const areaDoc = await areaRef.get();
    
    if (!areaDoc.exists) {
      console.error('파견 지역을 찾을 수 없습니다:', characterData.dispatchAreaId);
      return null;
    }

    const areaData = areaDoc.data() as ExplorationArea;
    const now = new Date();

    // 보상 계산
    let rewards: IDispatchHistory['rewards'] = {};

    // 1. 랜덤 조사 지문 선택
    if (areaData.investigationTexts && areaData.investigationTexts.length > 0) {
      const randomIndex = Math.floor(Math.random() * areaData.investigationTexts.length);
      rewards.investigationText = areaData.investigationTexts[randomIndex];
    }

    // 2. 아이템 보상 (dispatchOptions에서 가져오기)
    const itemChance = areaData.rewardSettings?.itemChance ?? 15;
    const shouldGetItem = Math.random() * 100 < itemChance;

    if (shouldGetItem && areaData.dispatchOptions && areaData.dispatchOptions.length > 0) {
      const possibleItemIds = areaData.dispatchOptions.flatMap(opt => opt.rewardItemIds || []);
      
      if (possibleItemIds.length > 0) {
        const randomItemIndex = Math.floor(Math.random() * possibleItemIds.length);
        const selectedItemId = possibleItemIds[randomItemIndex];
        
        const itemData = await getItemData({ itemID: selectedItemId });
        
        if (itemData) {
          // 캐릭터 인벤토리에 아이템 추가
          await parchaseItem({
            collectionPath: CollectionPath.PHOTOCARD,
            photocardID: characterId,
            data: { ...itemData, price: 0, id: selectedItemId },
            amount: 1,
            buyerID: userId,
          });

          rewards.itemId = selectedItemId;
          rewards.itemTitle = itemData.title;
          rewards.itemImgURL = itemData.imgURL;
        }
      }
    }

    // 파견 히스토리 생성
    const dispatchHistory: IDispatchHistory = {
      id: `dispatch-${Date.now()}`,
      areaId: characterData.dispatchAreaId,
      areaName: areaData.name,
      startedAt: characterData.dispatchStartedAt || now,
      endedAt: now,
      rewards,
    };

    // 기존 히스토리 가져오기
    const existingHistory = characterData.dispatchHistory || [];

    // 캐릭터 업데이트 (파견 상태 초기화 + 히스토리 추가)
    await characterRef.update({
      dispatchAreaId: '',
      isDispatched: false,
      dispatchStartedAt: null,
      dispatchDuration: 0,
      dispatchHistory: [...existingHistory, dispatchHistory],
    });

    console.log(`✅ 캐릭터 ${characterId} 파견 완료: ${areaData.name}`);
    return dispatchHistory;
  } catch (error) {
    console.error('파견 완료 처리 실패:', error);
    throw error;
  }
}

/**
 * 파견 취소하기
 */
export async function cancelDispatch({
  characterId,
  collectionPath,
}: {
  characterId: string;
  collectionPath: string;
}): Promise<boolean> {
  try {
    const characterRef = fuego.db.collection(collectionPath).doc(characterId);

    await characterRef.update({
      dispatchAreaId: '',
      isDispatched: false,
      dispatchStartedAt: null,
      dispatchDuration: 0,
    });

    console.log(`✅ 캐릭터 ${characterId} 파견 취소`);
    return true;
  } catch (error) {
    console.error('파견 취소 실패:', error);
    throw error;
  }
}

/**
 * 특정 지역에 파견 중인 캐릭터 목록 가져오기
 */
export async function getDispatchedCharactersByArea({
  areaId,
}: {
  areaId: string;
}): Promise<IPhotoCard[]> {
  try {
    const snapshot = await fuego.db
      .collection(CollectionPath.PHOTOCARD)
      .where('isDispatched', '==', true)
      .where('dispatchAreaId', '==', areaId)
      .get();

    const characters: IPhotoCard[] = [];
    snapshot.forEach(doc => {
      characters.push({ id: doc.id, ...doc.data() } as IPhotoCard);
    });

    return characters;
  } catch (error) {
    console.error('파견 중인 캐릭터 조회 실패:', error);
    return [];
  }
}

/**
 * 내 캐릭터 목록 가져오기 (파견 가능 여부 포함)
 */
export async function getMyCharacters({
  userId,
  collectionPath,
}: {
  userId: string;
  collectionPath: string;
}): Promise<IPhotoCard[]> {
  try {
    const snapshot = await fuego.db
      .collection(collectionPath)
      .where('owner', '==', userId)
      .get();

    const characters: IPhotoCard[] = [];
    snapshot.forEach(doc => {
      characters.push({ id: doc.id, ...doc.data() } as IPhotoCard);
    });

    return characters;
  } catch (error) {
    console.error('내 캐릭터 조회 실패:', error);
    return [];
  }
}

/**
 * 날짜/시간 값을 Date 객체로 변환
 * Firestore Timestamp, Date, string, number 등 다양한 형태를 처리
 */
function toDate(value: any): Date {
  if (!value) return new Date();
  
  // 이미 Date 객체인 경우
  if (value instanceof Date) return value;
  
  // Firestore Timestamp 객체 (toDate 메서드가 있는 경우)
  if (typeof value?.toDate === 'function') return value.toDate();
  
  // seconds와 nanoseconds를 가진 Firestore Timestamp 형태
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
  }
  
  // 숫자 (밀리초 timestamp)
  if (typeof value === 'number') return new Date(value);
  
  // 문자열
  if (typeof value === 'string') return new Date(value);
  
  return new Date();
}

/**
 * 파견 남은 시간 계산 (로컬 기반)
 * @param startedAt - 파견 시작 시간
 * @param duration - 파견 소요 시간 (분)
 * @returns 남은 시간 (밀리초), 0 이하면 완료
 */
export function calculateRemainingTime(
  startedAt: Date | string | any,
  duration: number,
): number {
  const startTime = toDate(startedAt);
  const endTime = new Date(startTime.getTime() + duration * 60 * 1000);
  const now = new Date();
  
  return endTime.getTime() - now.getTime();
}

/**
 * 파견 완료 여부 확인 (로컬 기반)
 */
export function isDispatchCompleted(
  startedAt: Date | string | any,
  duration: number,
): boolean {
  return calculateRemainingTime(startedAt, duration) <= 0;
}

/**
 * 남은 시간을 포맷팅 (시:분:초)
 */
export function formatRemainingTime(remainingMs: number): string {
  if (remainingMs <= 0) return '완료';
  
  const totalSeconds = Math.floor(remainingMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}시간 ${minutes}분 ${seconds}초`;
  } else if (minutes > 0) {
    return `${minutes}분 ${seconds}초`;
  } else {
    return `${seconds}초`;
  }
}





